import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Film,
  Clock,
  Calendar,
  Globe,
  Star,
  PlayCircle,
  ArrowLeft,
  Info,
  Users,
  Ticket,
  Loader,
  Share2,
  Heart,
  HeartOff,
  MapPin,
} from "lucide-react";
// Using fetch instead of axios

const MovieDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [availableShows, setAvailableShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(false);

  // BookMyShow API constants
  const cc = "GCMS";
  const subRegionCode = "BEEL";

  useEffect(() => {
    const fetchMovieDetails = async () => {
      try {
        setLoading(true);
        // Fetch movie details from old API
        const response = await fetch(
          `https://apigrand.dodunsoftsolutions.com/api/movie/${id}`
        );
        const responseData = await response.json();
        console.log("Movie data:", responseData);

        const movieData = responseData.data;
        setMovie({
          ...movieData,
          rating: movieData.rating || 4.0,
          genre: Array.isArray(movieData.genre)
            ? movieData.genre
            : movieData.genre
              ? [movieData.genre]
              : [],
        });

        // Now fetch shows from BookMyShow API
        await fetchBookMyShowData(movieData.movieName);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching movie details:", err);
        setError("Failed to fetch movie details. Please try again later.");
        setLoading(false);
      }
    };

    fetchMovieDetails();
  }, [id]);

  const fetchBookMyShowData = async (movieName) => {
    try {
      setShowsLoading(true);
      const allShows = [];

      // 1. Fetch all events (movies)
      const eventRes = await fetch(
        `https://cinemas.bookmyshow.com/api/getDEData?cmd=IBVGETEVENTBYSUBREGION&cc=${cc}&sr=${subRegionCode}&et=MT&f=json`
      );
      const eventData = await eventRes.json();
      const events = eventData?.BookMyShow?.arrEvents || [];

      // 2. Find matching event by movie name
      const matchingEvent = events.find(
        (event) =>
          event.EventTitle.toLowerCase().includes(movieName.toLowerCase()) ||
          movieName.toLowerCase().includes(event.EventTitle.toLowerCase())
      );

      if (!matchingEvent) {
        console.log(`No matching event found for movie: ${movieName}`);
        setAvailableDates([]);
        setAvailableShows([]);
        setShowsLoading(false);
        return;
      }

      console.log(
        `Found matching event: ${matchingEvent.EventTitle} for movie: ${movieName}`
      );

      const eventCode = matchingEvent.EventCode;

      // 3. Fetch show dates for the matching event
      const dateRes = await fetch(
        `https://cinemas.bookmyshow.com/api/getDEData?cmd=IBVGETSHOWDATESBYEVENT&cc=${cc}&sr=${subRegionCode}&et=MT&ec=${eventCode}&f=json`
      );
      const dateData = await dateRes.json();
      const showDates = dateData?.BookMyShow?.arrShowDates || [];

      for (const date of showDates) {
        const dateCode = date.ShowDateCode;
        const dateDisplay = date.ShowDateDisplay;

        // 4. Fetch showtimes for event + date
        const timeRes = await fetch(
          `https://cinemas.bookmyshow.com/api/getDEData?cmd=IBVGETSHOWTIMESBYEVENT&cc=${cc}&dc=${dateCode}&ec=${eventCode}&sr=${subRegionCode}&et=MT&f=json`
        );
        const timeData = await timeRes.json();
        const showtimes = timeData?.BookMyShow?.arrShowTimes || [];
        const venue = timeData?.BookMyShow?.arrVenues?.[0] || null;

        if (showtimes.length > 0) {
          allShows.push({
            eventTitle: matchingEvent.EventTitle,
            eventCode,
            venue,
            dateCode,
            dateDisplay,
            showtimes,
          });
        }
      }

      // Process dates
      const uniqueDates = [
        ...new Set(allShows.map((show) => show.dateDisplay)),
      ].sort();
      setAvailableDates(uniqueDates);

      // Set default date (today if available, otherwise first available)
      const today = new Date()
        .toLocaleDateString("en-IN", {
          weekday: "short",
          day: "2-digit",
          month: "short",
        })
        .replace(/,/g, "");

      const todayDate = uniqueDates.find((date) =>
        date.includes(today.split(" ")[1])
      );
      const defaultDate = todayDate || uniqueDates[0];

      if (defaultDate) {
        setSelectedDate(defaultDate);
        // Filter shows for default date
        const defaultDateShows = allShows.filter(
          (show) => show.dateDisplay === defaultDate
        );
        setAvailableShows(defaultDateShows);
      }

      setShowsLoading(false);
    } catch (err) {
      console.error("Error fetching BookMyShow data:", err);
      setAvailableDates([]);
      setAvailableShows([]);
      setShowsLoading(false);
    }
  };

  // Update available shows when selected date changes
  useEffect(() => {
    if (!selectedDate || !movie) return;

    const fetchShowsForDate = async () => {
      try {
        setShowsLoading(true);
        await fetchBookMyShowData(movie.movieName);
      } catch (err) {
        console.error("Error fetching shows for date:", err);
        setAvailableShows([]);
        setShowsLoading(false);
      }
    };

    // Filter existing shows by selected date instead of refetching
    const filteredShows = availableShows.filter(
      (show) => show.dateDisplay === selectedDate
    );
    if (filteredShows.length === 0 && availableShows.length > 0) {
      // Need to refetch for this date
      fetchShowsForDate();
    }
  }, [selectedDate]);

  // Helper function to get time range
  const getTimeRange = (timeStr) => {
    const hour = parseInt(timeStr.split(":")[0]);
    if (hour >= 6 && hour < 12) return "morning";
    if (hour >= 12 && hour < 17) return "afternoon";
    if (hour >= 17 && hour < 21) return "evening";
    return "night";
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format duration to hours and minutes
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins > 0 ? `${mins}m` : ""}`;
  };

  // Format show date for display
  const formatShowDate = (dateString) => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (dateString.includes(today.getDate().toString().padStart(2, "0"))) {
      return "Today";
    } else if (
      dateString.includes(tomorrow.getDate().toString().padStart(2, "0"))
    ) {
      return "Tomorrow";
    }
    return dateString;
  };

  // Toggle favorite status
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  // Book tickets for a specific show
  const bookTickets = (show, showtime) => {
    const bookingUrl = `https://in.bookmyshow.com/movies/una/seat-layout/${show.eventCode}/${showtime.VenueCode}/${showtime.SessionId}/${show.dateCode}`;
    window.open(bookingUrl, "_blank");
  };

  // Generate available dates for show selection
  const getAvailableDates = () => {
    if (availableDates.length === 0) {
      return [];
    }

    return availableDates.map((date) => ({
      date,
      display: formatShowDate(date),
    }));
  };

  // Share movie with friends
  const shareMovie = () => {
    if (navigator.share) {
      navigator
        .share({
          title: movie.movieName,
          text: `Check out ${movie.movieName} at Una Cinemas!`,
          url: window.location.href,
        })
        .catch((error) => console.log("Error sharing:", error));
    } else {
      alert(`Share this link: ${window.location.href}`);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <div className="relative">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-gray-200 rounded-full animate-spin"></div>
          <div
            className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-t-transparent rounded-full animate-spin absolute top-0"
            style={{ borderColor: "#671111", borderTopColor: "transparent" }}
          ></div>
        </div>
        <p className="mt-4 text-xl font-medium" style={{ color: "#6e7581" }}>
          Loading movie details...
        </p>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <div className="p-6 max-w-md bg-white rounded-xl shadow-lg text-center border border-gray-200">
          <h2 className="text-2xl font-bold mb-4" style={{ color: "#da2526" }}>
            Error
          </h2>
          <p style={{ color: "#6e7581" }}>{error || "Movie not found"}</p>
          <button
            className="mt-6 px-6 py-3 text-white rounded-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#da2526" }}
            onClick={() => navigate("/")}
          >
            Back to Movies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Movie Banner/Hero Section */}
      <div
        className="relative text-white overflow-hidden"
        style={{ background: "linear-gradient(to bottom, #671111, #000000)" }}
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src={
              movie.image
                ? movie.image.startsWith("http")
                  ? movie.image
                  : `https://apigrand.dodunsoftsolutions.com${movie.image}`
                : "/api/placeholder/1200/600"
            }
            alt={movie.movieName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "/api/placeholder/1200/600";
            }}
          />
        </div>

        {/* Content */}
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-8 sm:py-12 relative z-10">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center px-3 py-2 mb-4 sm:mb-6 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-sm sm:text-base transition-all"
          >
            <ArrowLeft size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Back to Movies</span>
            <span className="sm:hidden">Back</span>
          </button>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Movie Poster */}
            <div className="w-full sm:w-80 lg:w-1/4 mx-auto lg:mx-0">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={
                    movie.image
                      ? movie.image.startsWith("http")
                        ? movie.image
                        : `https://apigrand.dodunsoftsolutions.com${movie.image}`
                      : "/api/placeholder/1200/600"
                  }
                  alt={movie.movieName}
                  className="w-full aspect-[2/3] object-cover"
                  onError={(e) => {
                    e.target.src = "/api/placeholder/300/450";
                  }}
                />
              </div>
            </div>

            {/* Movie Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  {movie.movieName}
                </h1>
                {movie.certificate && (
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full self-center lg:self-start ${movie.certificate === "U"
                      ? "bg-green-100 text-green-800"
                      : movie.certificate === "U/A"
                        ? "bg-red-100 text-red-800"
                        : movie.certificate === "PG-13"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                  >
                    {movie.certificate}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-center lg:justify-start text-yellow-400 mb-4">
                <Star className="w-5 h-5 fill-yellow-400" />
                <span className="ml-2 text-sm font-medium text-white">
                  IMDb {movie?.imdbRating ? `${movie.imdbRating}/10` : "--/10"}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
                {movie.genre.map((genre, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-gray-800 rounded-full text-sm"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-sm sm:text-base">
                <div className="flex items-center justify-center lg:justify-start">
                  <Clock className="w-5 h-5 mr-2 text-gray-300" />
                  <span>{formatDuration(movie.duration)}</span>
                </div>

                <div className="flex items-center justify-center lg:justify-start">
                  <Calendar className="w-5 h-5 mr-2 text-gray-300" />
                  <span>Released: {formatDate(movie.releaseDate)}</span>
                </div>

                <div className="flex items-center justify-center lg:justify-start">
                  <Globe className="w-5 h-5 mr-2 text-gray-300" />
                  <span>{movie.language}</span>
                </div>

                <div className="flex items-center justify-center lg:justify-start">
                  <Users className="w-5 h-5 mr-2 text-gray-300" />
                  <span>Director: {movie.director || "Not available"}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
                {movie.trailerLink && (
                  <a
                    href={movie.trailerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 rounded-lg flex items-center justify-center transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "#da2526" }}
                  >
                    <PlayCircle className="mr-2" size={20} />
                    Watch Trailer
                  </a>
                )}

                <button
                  onClick={toggleFavorite}
                  className={`px-6 py-3 rounded-lg flex items-center justify-center transition-opacity hover:opacity-90 ${isFavorite ? "bg-pink-600" : "bg-gray-700"
                    }`}
                >
                  {isFavorite ? (
                    <>
                      <HeartOff className="mr-2" size={20} />
                      <span className="hidden sm:inline">
                        Remove from Favorites
                      </span>
                      <span className="sm:hidden">Unfavorite</span>
                    </>
                  ) : (
                    <>
                      <Heart className="mr-2" size={20} />
                      <span className="hidden sm:inline">Add to Favorites</span>
                      <span className="sm:hidden">Favorite</span>
                    </>
                  )}
                </button>

                <button
                  onClick={shareMovie}
                  className="px-6 py-3 bg-indigo-600 hover:opacity-90 rounded-lg flex items-center justify-center transition-opacity"
                >
                  <Share2 className="mr-2" size={20} />
                  Share
                </button>
              </div>

              {/* Synopsis */}
              <div>
                <h2 className="text-xl font-semibold mb-3">Synopsis</h2>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  {movie.description ||
                    "No synopsis available for this movie. Come experience the magic on the big screen!"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show Times Section */}
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8">
        <h2 className="text-xl sm:text-2xl font-bold text-black mb-6 flex items-center">
          <Ticket className="mr-2" style={{ color: "#da2526" }} />
          Book Tickets
        </h2>

        {/* Date Selection */}
        {availableDates.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <h3
              className="text-lg font-medium mb-3"
              style={{ color: "#6e7581" }}
            >
              Select Date
            </h3>
            <div className="flex overflow-x-auto pb-2 gap-2 -mx-1">
              {getAvailableDates().map((day) => (
                <button
                  key={day.date}
                  onClick={() => setSelectedDate(day.date)}
                  className={`px-4 py-3 rounded-lg flex-shrink-0 font-medium transition-all text-sm sm:text-base ${selectedDate === day.date
                    ? "text-white shadow-lg"
                    : "border-2 border-gray-300 hover:border-gray-400 text-black bg-white"
                    }`}
                  style={
                    selectedDate === day.date
                      ? { backgroundColor: "#671111" }
                      : {}
                  }
                >
                  {day.display}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Show Times */}
        <div>
          <h3 className="text-lg font-medium mb-4" style={{ color: "#6e7581" }}>
            Available Shows
          </h3>

          {showsLoading ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="relative inline-block">
                <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin"></div>
                <div
                  className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin absolute top-0"
                  style={{
                    borderColor: "#671111",
                    borderTopColor: "transparent",
                  }}
                ></div>
              </div>
              <p className="mt-3" style={{ color: "#6e7581" }}>
                Loading shows...
              </p>
            </div>
          ) : availableShows.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="text-4xl mb-4">🎭</div>
              <p style={{ color: "#6e7581" }}>
                {availableDates.length === 0
                  ? "No shows available for this movie currently."
                  : "No shows available for this date."}
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {availableShows.map((show, index) => (
                <div
                  key={`${show.eventCode}-${show.dateCode}`}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="text-center sm:text-left">
                        <h4 className="text-lg sm:text-xl font-bold text-black mb-2">
                          {show.eventTitle}
                        </h4>
                        <div
                          className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm"
                          style={{ color: "#6e7581" }}
                        >
                          <div className="flex items-center justify-center sm:justify-start space-x-2">
                            <MapPin size={16} />
                            <span className="font-medium">
                              {show.venue?.VenueName}
                            </span>
                          </div>
                          <div className="flex items-center justify-center sm:justify-start space-x-2">
                            <Calendar size={16} />
                            <span className="font-medium">
                              {formatShowDate(show.dateDisplay)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        className="px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-white self-center sm:self-start"
                        style={{ backgroundColor: "#671111" }}
                      >
                        {show.showtimes.length} Shows
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4">
                      <h5
                        className="text-sm font-medium mb-3"
                        style={{ color: "#6e7581" }}
                      >
                        Available Shows ({show.showtimes.length})
                      </h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        {show.showtimes.map((showtime, i) => {
                          const timeRange = getTimeRange(
                            showtime.ShowTimeDisplay
                          );
                          const timeRangeStyles = {
                            morning: {
                              backgroundColor: "#da2526",
                              color: "white",
                            },
                            afternoon: {
                              backgroundColor: "#da2526",
                              color: "white",
                            },
                            evening: {
                              backgroundColor: "#671111",
                              color: "white",
                            },
                            night: {
                              backgroundColor: "#671111",
                              color: "white",
                            },
                          };

                          return (
                            <button
                              key={i}
                              onClick={() => bookTickets(show, showtime)}
                              className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-bold text-xs sm:text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg transform text-center"
                              style={timeRangeStyles[timeRange]}
                              title={`${timeRange.charAt(0).toUpperCase() +
                                timeRange.slice(1)
                                } show - Book now`}
                            >
                              {showtime.ShowTimeDisplay}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieDetails;
